import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Modal } from '../Modal/Modal';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import ExpandableSection from '../ExpandableSection/ExpandableSection';
import { geminiService, GeminiImageGenerationRequest } from '../../services/geminiService';
import { toastService } from '../../services/ToastService';
import { ASPECT_RATIO_OPTIONS, getImageEditorPrompts, ImageEditorPrompt } from '../../pages/SocialContentPage/prompts/imageEditorPrompts';
import FileUploadWrapper from '../FileUploadWrapper/FileUploadWrapper';
import CONFIG from '../../config/config';

type Status = "idle" | "uploading" | "processing" | "completed" | "error";

type PlaceholderValues = Record<string, string>;

type GeneratedAsset = {
  id: string;
  url: string;
  createdAt: string;
  isOriginal?: boolean;
};

interface ImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialImageUrl?: string;
  onSave: (editedImageUrl: string) => void;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Icon mapping for filter group tabs
const getGroupIcon = (group: string) => {
  const iconClass = "w-3 h-3 inline-block mr-1.5";
  switch (group) {
    case "Model":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case "Posture & Motion":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case "Scene":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      );
    case "Lighting & Style":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case "Composition / Physics":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      );
    case "Technical":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    default:
      return null;
  }
};

// Icon mapping for filter categories
const getCategoryIcon = (category: string) => {
  const iconClass = "w-3 h-3 inline-block mr-1.5";
  switch (category) {
    case "Hair Color":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      );
    case "Hair Length & Texture":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      );
    case "Hair Style":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      );
    case "Makeup":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      );
    case "Eye Color":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      );
    case "Hair & Makeup":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      );
    case "Expressions":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "Poses":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case "Posture & Motion":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case "Pose Details":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      );
    case "Head Tilt":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
        </svg>
      );
    case "Arms & Hands":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      );
    case "Legs":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      );
    case "Posture":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    case "Body Direction":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      );
    case "Motion":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case "Body Proportions":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    case "Skin Detail":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    case "Backgrounds":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case "Classic & Architectural":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      );
    case "Natural & Outdoor":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "Modern & Urban":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    case "Dreamlike & Seasonal":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    case "Destination & Cultural":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case "Lighting":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case "Style":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      );
    case "Framing":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      );
    case "Camera Angle":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case "Targeted Edit":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case "Cleanup / Retouch":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      );
    case "Art Direction":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      );
  }
};

export default function ImageEditorModal({ isOpen, onClose, initialImageUrl, onSave }: ImageEditorModalProps) {
  const { t } = useTranslation();
  const [modelImage, setModelImage] = useState<string>(initialImageUrl || '');
  const [prompt, setPrompt] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<ImageEditorPrompt | null>(null);
  const [placeholderValues, setPlaceholderValues] = useState<PlaceholderValues>({});
  const [aspectRatio, setAspectRatio] = useState<string>(ASPECT_RATIO_OPTIONS.find(opt => opt.value === "1:1")?.value || ASPECT_RATIO_OPTIONS[0].value);
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [editedImageUrl, setEditedImageUrl] = useState<string>('');
  const [activeFilterGroup, setActiveFilterGroup] = useState<string>("Model");
  const [outputs, setOutputs] = useState<GeneratedAsset[]>([]);

  const templates = useMemo(() => getImageEditorPrompts(t), [t]);

  // Initialize with initial image when modal opens
  useEffect(() => {
    if (isOpen && initialImageUrl) {
      setModelImage(initialImageUrl);
      setEditedImageUrl('');
      setPrompt('');
      setSelectedTemplate(null);
      setPlaceholderValues({});
      setStatus("idle");
      setProgress(0);
      setError(null);
      // Add original image to history
      setOutputs([{
        id: `original-${Date.now()}`,
        url: initialImageUrl,
        createdAt: new Date().toISOString(),
        isOriginal: true
      }]);
    }
  }, [isOpen, initialImageUrl]);

  const onFileUpload = async (files: File | FileList) => {
    const fileArray = files instanceof FileList ? Array.from(files) : [files];
    if (fileArray.length === 0) return;
    
    const url = await readFileAsDataUrl(fileArray[0]);
    setModelImage(url);
    setEditedImageUrl('');
    
    // Add uploaded image to history
    const asset: GeneratedAsset = {
      id: `original-${Date.now()}`,
      url: url,
      createdAt: new Date().toISOString(),
      isOriginal: true
    };
    setOutputs([asset]); // Replace history with new original
  };

  const getFinalPrompt = (template: ImageEditorPrompt, values: PlaceholderValues): string => {
    let finalPrompt = template.prompt;
    Object.entries(values).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      finalPrompt = finalPrompt.replace(new RegExp(placeholder, 'g'), value);
    });
    return finalPrompt;
  };

  const generate = async (promptToUse?: string) => {
    let finalPrompt = promptToUse || prompt;
    if (selectedTemplate?.placeholders) {
      finalPrompt = getFinalPrompt(selectedTemplate, placeholderValues);
    }
    
    if (!modelImage || (!selectedTemplate && finalPrompt.length <= 2)) {
      toastService.showToast('Please provide a prompt or select a template', 'error');
      return;
    }
    
    setStatus("uploading");
    setProgress(10);
    setError(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setProgress(30);
      setStatus("processing");

      const imageData = modelImage.split(',')[1];
      const request: GeminiImageGenerationRequest = {
        prompt: finalPrompt,
        imageData,
        model: "gemini-2.5-flash-image",
        aspectRatio
      };

      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(90, prev + Math.random() * 10));
      }, 200);

      const response = await geminiService.generateImage(request);

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success && response.imageUrl) {
        setStatus("completed");
        setEditedImageUrl(response.imageUrl);
        
        // Add to history
        const asset: GeneratedAsset = {
          id: Math.random().toString(36).slice(2),
          url: response.imageUrl,
          createdAt: new Date().toISOString(),
          isOriginal: false
        };
        setOutputs(prev => [asset, ...prev].slice(0, 12)); // Keep last 12 items
        
        toastService.showToast('Image edited successfully!', 'success');
      } else {
        setStatus("error");
        setError(response.error || "Failed to generate image");
        toastService.showToast(response.error || 'Failed to edit image', 'error');
      }
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      toastService.showToast('Error editing image', 'error');
    }
  };

  const handleTemplateSelect = (template: ImageEditorPrompt) => {
    setSelectedTemplate(template);
    
    if (template.placeholders) {
      const initialValues: PlaceholderValues = {};
      Object.entries(template.placeholders).forEach(([key, options]) => {
        initialValues[key] = (options as string[])[0] || "";
      });
      setPlaceholderValues(initialValues);
      setPrompt(getFinalPrompt(template, initialValues));
    } else {
      setPlaceholderValues({});
      setPrompt(template.prompt);
    }
  };

  const handleSave = () => {
    if (editedImageUrl) {
      onSave(editedImageUrl);
      onClose();
    } else {
      toastService.showToast('Please generate an edited image first', 'error');
    }
  };

  const isReadyToGenerate = modelImage && (!!selectedTemplate || prompt.length > 2);

  if (!isOpen) return null;

  return (
    <Modal
      modalTitle="Edit Image with AI"
      canClose
      onClose={onClose}
    >
      <div className="space-y-6 max-h-[80vh] overflow-y-auto">
        {/* Preview Section */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Preview</h3>
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 flex items-center justify-center min-h-[200px]">
            {editedImageUrl ? (
              <img 
                src={editedImageUrl} 
                alt="Edited" 
                className="max-w-full max-h-64 object-contain rounded"
              />
            ) : modelImage ? (
              <img 
                src={modelImage} 
                alt="Original" 
                className="max-w-full max-h-64 object-contain rounded"
              />
            ) : (
              <p className="text-gray-400">No image loaded</p>
            )}
          </div>
          {status !== "idle" && status !== "completed" && status !== "error" && (
            <div className="mt-2">
              <div className="h-2 w-full bg-gray-200 rounded">
                <div className="h-2 bg-blue-500 rounded" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-1">{Math.round(progress)}% - {status}</p>
            </div>
          )}
          {status === "error" && error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
              {error}
            </div>
          )}
          
          {/* History of Changes */}
          {outputs.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">History</h4>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {outputs.map((output) => (
                  <div
                    key={output.id}
                    onClick={() => {
                      setModelImage(output.url);
                      setEditedImageUrl('');
                      toastService.showToast('Image selected as base', 'success');
                    }}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      (editedImageUrl && output.url === editedImageUrl) || (!editedImageUrl && output.url === modelImage)
                        ? 'border-darker-gold-300 shadow-md'
                        : 'border-gray-200 hover:border-darker-gold-200'
                    }`}
                  >
                    <div className="relative w-full h-full bg-gray-100">
                      <img
                        src={output.url}
                        alt={output.isOriginal ? 'Original' : 'Edited'}
                        className="w-full h-full object-cover"
                      />
                      {output.isOriginal && (
                        <div className="absolute top-1 left-1 px-1 py-0.5 bg-darker-gold-300 text-white text-[8px] font-semibold rounded">
                          Original
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Image Upload */}
        {!modelImage && (
          <div>
            <FileUploadWrapper
              label="Upload Image"
              onChange={onFileUpload}
              allowedTypes={CONFIG.IMAGE_TYPES}
              allowedMultipleFiles={false}
            />
          </div>
        )}

        {/* Templates */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Quick Edits</h3>
          
          {/* Filter Group Tabs */}
          <div className="mb-3">
            <div className="flex flex-wrap gap-1.5">
              {["Model", "Scene", "Posture & Motion", "Lighting & Style", "Composition / Physics", "Technical"].map((group) => (
                <button 
                  key={group}
                  onClick={() => setActiveFilterGroup(group)}
                  className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors flex items-center ${
                    activeFilterGroup === group
                      ? 'bg-darker-gold-300 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getGroupIcon(group)}
                  <span>
                    {group === "Model" && t('social_content.template.groups.model')}
                    {group === "Posture & Motion" && (t('social_content.template.groups.posture_motion') || "Posture & Motion")}
                    {group === "Scene" && t('social_content.template.groups.scene')}
                    {group === "Lighting & Style" && t('social_content.template.groups.lighting')}
                    {group === "Composition / Physics" && t('social_content.template.groups.composition')}
                    {group === "Technical" && t('social_content.template.groups.technical')}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Filter Options */}
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
            {Object.entries(
              templates
                .filter((template: ImageEditorPrompt) => template.group === activeFilterGroup)
                .reduce((acc, template) => {
                  if (!acc[template.category]) {
                    acc[template.category] = [];
                  }
                  acc[template.category].push(template);
                  return acc;
                }, {} as Record<string, ImageEditorPrompt[]>)
            ).map(([category, categoryTemplates]) => (
              <div key={category} className="mb-4 last:mb-0">
                <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                  {getCategoryIcon(category)}
                  <span>{category}</span>
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {categoryTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'bg-darker-gold-300 hover:bg-darker-gold-100 text-white shadow-sm'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                      title={template.description}
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Placeholder Options */}
        {selectedTemplate?.placeholders && Object.keys(selectedTemplate.placeholders).length > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-xs font-semibold text-gray-700 mb-3">Configure Options</h4>
            <div className="space-y-3">
              {Object.entries(selectedTemplate.placeholders).map(([key, options]) => {
                const label = key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, l => l.toUpperCase());
                
                return (
                  <div key={key}>
                    <label className="text-xs font-medium text-gray-700 block mb-1">
                      {label}
                    </label>
                    <select
                      value={placeholderValues[key] || options[0] || ""}
                      onChange={(e) => {
                        const updatedValues = { ...placeholderValues, [key]: e.target.value };
                        setPlaceholderValues(updatedValues);
                        setPrompt(getFinalPrompt(selectedTemplate, updatedValues));
                      }}
                      className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-card text-foreground"
                    >
                      {options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Custom Prompt */}
        <ExpandableSection
          defaultState={false}
          collapsedContent={(isExpanded) => (
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-600" />
              )}
              <h3 className="text-sm font-semibold cursor-pointer">Custom Prompt</h3>
            </div>
          )}
          expandedContent={
            <div id="expandable-content">
              <textarea 
                className="w-full rounded-md border p-2 text-sm" 
                rows={3} 
                placeholder="Describe how you want to edit the image..." 
                value={prompt} 
                onChange={(e) => setPrompt(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          }
        />

        {/* Aspect Ratio */}
        <div>
          <label className="text-sm font-semibold block mb-2">Aspect Ratio</label>
          <div className="flex flex-wrap gap-1.5">
            {ASPECT_RATIO_OPTIONS.slice(0, 5).map((option) => (
              <button
                key={option.value}
                className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                  aspectRatio === option.value
                    ? 'bg-darker-gold-300 hover:bg-darker-gold-100 text-white shadow-sm'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
                onClick={() => setAspectRatio(option.value)}
                title={option.tags && option.tags.length > 0 ? option.tags.join(", ") : undefined}
              >
                {option.label}
                {option.tags && option.tags.length > 0 && (
                  <span className="ml-1 opacity-75">
                    ({option.tags.join(", ")})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-center pt-4 border-t">
          <PrimaryButton 
            text="Generate Edit"
            fn={() => generate()}
            isDisabled={!isReadyToGenerate || status === "processing" || status === "uploading"}
          />
          <PrimaryButton 
            text="Save Edited Image"
            fn={handleSave}
            isDisabled={!editedImageUrl}
          />
        </div>
      </div>
    </Modal>
  );
}

